'use client';

import { addNextjsError } from '@datadog/browser-rum-nextjs';
import { useEffect } from 'react';

interface GlobalErrorProps {
  error: Error;
  reset: () => void;
}

/**
 * RootLayout이 깨졌을 때의 최후 fallback. emotion·Provider·Stack·hooks가 모두 죽은 것으로 보고
 * 인라인 스타일 + native 엘리먼트로만 구성하며, 외부 import 의존을 0으로 둔다 — `@/env`는 검증
 * throw 시 이 fallback까지 함께 깨뜨리므로 process.env를 직접 읽고, 진입 브랜드 홈 URL도 util
 * 대신 storage에서 직접 읽는다(의도된 중복).
 */
function readBrandHomeUrlFromStorage(): string | undefined {
  try {
    const stored = window.sessionStorage.getItem('apply.salesLandingInfo');
    if (!stored) return undefined;
    const parsed: unknown = JSON.parse(stored);
    if (parsed && typeof parsed === 'object' && 'brand' in parsed) {
      const brand = (parsed as { brand?: unknown }).brand;
      if (brand && typeof brand === 'object' && 'homeUrl' in brand) {
        const { homeUrl } = brand as { homeUrl?: unknown };
        if (typeof homeUrl === 'string') return homeUrl;
      }
    }
  } catch {
    // sessionStorage 접근 불가·JSON 손상 시 undefined → 중립 브랜드 홈으로 폴백
  }
  return undefined;
}

/**
 * 외부 이동 전 http/https만 허용 — 손상·변조된 storage 값으로 `javascript:` 등 임의 스킴이
 * 실행되지 않게 막는다. isAllowedExternalUrl과 동일 검증이나 의존성 0 정책상 인라인 재현.
 */
function isHttpUrl(value: string): boolean {
  try {
    const { protocol } = new URL(value);
    return protocol === 'http:' || protocol === 'https:';
  } catch {
    return false;
  }
}

export default function GlobalError({ error, reset }: GlobalErrorProps) {
  // RootLayout이 깨진 최후 fallback이라도 RUM이 살아 있으면 에러를 리포트한다.
  // RUM 미초기화·SDK 미로드 등 어떤 실패도 이 fallback을 깨뜨리지 않도록 try/catch로 격리.
  useEffect(
    function reportToDatadog() {
      try {
        addNextjsError(error);
      } catch {
        // 리포트 실패는 무시 — 마지막 fallback의 렌더를 막지 않는다.
      }
    },
    [error],
  );

  const handleExit = () => {
    if (typeof window === 'undefined') return;
    // 진입 브랜드 홈 URL(있으면) → 중립 브랜드(KANT) 홈으로 폴백. 하드코딩 URL은 두지 않으므로
    // env가 비어 URL이 없으면 이동하지 않는다.
    const homeUrl = readBrandHomeUrlFromStorage() ?? process.env.NEXT_PUBLIC_KANT_URL;
    if (homeUrl && isHttpUrl(homeUrl)) window.location.href = homeUrl;
  };

  return (
    <html lang="ko">
      <body
        style={{
          margin: 0,
          minHeight: '100dvh',
          display: 'flex',
          alignItems: 'center',
          justifyContent: 'center',
          padding: 24,
          fontFamily: 'system-ui, -apple-system, sans-serif',
          background: '#fff',
          color: '#1a1a1a',
        }}
      >
        <div style={{ maxWidth: 400, textAlign: 'center' }}>
          <h1 style={{ fontSize: 20, fontWeight: 700, margin: '0 0 12px' }}>
            알 수 없는 오류가 발생했어요.
          </h1>
          <p style={{ fontSize: 14, lineHeight: 1.5, color: '#525252', margin: '0 0 24px' }}>
            잠시 후 다시 시도해 주세요.
          </p>
          <div style={{ display: 'flex', flexDirection: 'column', gap: 8 }}>
            <button
              type="button"
              onClick={reset}
              style={{
                padding: '12px 16px',
                fontSize: 15,
                fontWeight: 600,
                color: '#fff',
                background: '#1a1a1a',
                border: 'none',
                borderRadius: 8,
                cursor: 'pointer',
              }}
            >
              다시 시도
            </button>
            <button
              type="button"
              onClick={handleExit}
              style={{
                padding: '12px 16px',
                fontSize: 15,
                fontWeight: 600,
                color: '#1a1a1a',
                background: '#fff',
                border: '1px solid #d4d4d4',
                borderRadius: 8,
                cursor: 'pointer',
              }}
            >
              메인으로 가기
            </button>
          </div>
        </div>
      </body>
    </html>
  );
}
